import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { PASSWORD_RULES_TEXT, validatePassword } from "../utils/password";
import { getGoogleConfig, startGoogleLogin } from "../utils/googleAuth";
import { getNextPath, rememberNext, withNext } from "../utils/authRedirect";
import { getPublicPath } from "../utils/seoPages";
import { trackEvent } from "../utils/analytics";

const LOGO = "https://res.cloudinary.com/drzrhdf7c/image/upload/v1780654890/sf-learning/brand/logo_sf_learning.png";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const { registerJWT } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  // Achat commencé sans compte : on reprend le paiement après l'inscription.
  // Inscription simple : on ouvre le catalogue pour que la personne choisisse
  // sa formation, plutôt qu'un tableau de bord encore vide.
  const nextPath = getNextPath();
  const afterRegisterPath = nextPath || getPublicPath(settings, "/catalog");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("sf_ref", ref);
    }
    getGoogleConfig().then((cfg) => setGoogleEnabled(!!cfg.enabled));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const strengthError = validatePassword(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    setLoading(true);
    try {
      await registerJWT(email, name, password);
      trackEvent("sign_up", { method: "email" });
      navigate(afterRegisterPath);
    } catch (err) {
      setError(err.response?.data?.detail || "Inscription échouée");
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth 2.0 standard (sans Emergent). Le bouton ne s'affiche que si
  // GOOGLE_CLIENT_ID/SECRET sont configurés côté backend (cf. /auth/google/config).
  const handleGoogle = async () => {
    try {
      // L'aller-retour Google fait perdre l'URL courante : on met la destination
      // de cote pour la retrouver au retour sur /auth/callback.
      rememberNext(nextPath);
      await startGoogleLogin();
    } catch (e) {
      setError("La connexion Google est momentanément indisponible.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-76px)] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="card p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <img src={LOGO} alt="SF Learning" className="h-16 w-16 mx-auto rounded-xl" />
          <h1 className="font-heading text-2xl font-black mt-3">Bienvenue chez SF Learning</h1>
          <p className="text-sm text-gray-500">Crée ton compte gratuitement</p>
        </div>

        {googleEnabled && (
          <>
            <button onClick={handleGoogle} className="btn-ghost w-full justify-center mb-4" data-testid="google-register-btn">
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continuer avec Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">ou par email</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label mb-1 block">Prénom & Nom</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="input" data-testid="register-name-input" placeholder="Marie Dupont" />
          </div>
          <div>
            <label className="label mb-1 block">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" data-testid="register-email-input" placeholder="ton@email.com" />
          </div>
          <div>
            <label className="label mb-1 block">Mot de passe</label>
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input" data-testid="register-password-input" placeholder="••••••••" />
            <p className="text-xs text-gray-500 mt-1">{PASSWORD_RULES_TEXT}</p>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit-btn">
            {loading ? "Création..." : "Créer mon compte"}
          </button>
        </form>

        <p className="text-sm text-center mt-6 text-gray-600">
          Déjà inscrit ? <Link to={withNext("/login", nextPath)} className="text-brand-green font-semibold" data-testid="goto-login-link">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}
